import React from 'react';
import { Button } from 'react-bootstrap';

const EVENT_LABELS = {
  login_success: 'Inloggning',
  login_failure: 'Misslyckad inloggning',
  logout: 'Utloggning',
  token_refresh: 'Session förnyad',
};

const EVENT_COLORS = {
  login_success: '#44814E',
  login_failure: '#dc3545',
  logout: '#737B8B',
  token_refresh: '#737B8B',
};

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('sv-SE', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function summariseUserAgent(ua) {
  if (!ua) return '—';
  let browser = 'Okänd webbläsare';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua) && !/Chromium|OPR/.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = 'Safari';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';

  let os = 'Okänt OS';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS X|Macintosh/.test(ua)) os = 'macOS';
  else if (/iPhone|iPad|iOS/.test(ua)) os = 'iOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Linux/.test(ua)) os = 'Linux';

  return `${browser} · ${os}`;
}

// Security section of the user settings — password/sign-in method management and
// login history. Sign-in methods and login events are fetched in the
// UserSettings shell (login history lazy-loads on first visit) and passed in.
const SecuritySection = ({
  signInMethods,
  email,
  passwordBusy,
  handleSendPasswordReset,
  openSetPasswordModal,
  loginEvents,
  loginEventsLoading,
}) => {
  const hasPassword = signInMethods?.hasPassword;
  const onlyGoogle = signInMethods && !signInMethods.hasPassword && signInMethods.hasGoogle;

  return (
    <>
      <div className="user-settings__section-header">
        <h2>Säkerhet</h2>
        <p>Hantera hur du loggar in på ditt konto.</p>
      </div>

      <div className="user-settings__card" id="losenord">
        <h5 style={{ margin: '0 0 0.375rem', fontSize: '1rem', fontWeight: 600 }}>
          {onlyGoogle ? 'Logga in med e-post och lösenord' : 'Lösenord'}
        </h5>

        {signInMethods === null && (
          <p style={{ color: '#737B8B', margin: 0, fontSize: '0.875rem' }}>
            Läser in inloggningsalternativ...
          </p>
        )}

        {hasPassword && (
          <>
            <p style={{ color: '#737B8B', margin: '0 0 1rem', fontSize: '0.875rem' }}>
              Skicka en återställningslänk till <strong>{email}</strong>.
            </p>
            <Button variant="primary" onClick={handleSendPasswordReset} disabled={passwordBusy}>
              {passwordBusy ? 'Skickar...' : 'Skicka återställningslänk'}
            </Button>
          </>
        )}

        {onlyGoogle && (
          <>
            <p style={{ color: '#737B8B', margin: '0 0 1rem', fontSize: '0.875rem' }}>
              Du loggar för tillfället in med Google. Vill du även kunna logga in
              med <strong>{email}</strong> och ett lösenord? Google-inloggningen
              fortsätter att fungera som vanligt.
            </p>
            <Button variant="primary" onClick={openSetPasswordModal} disabled={passwordBusy}>
              Aktivera lösenordsinloggning
            </Button>
          </>
        )}
      </div>

      <div className="user-settings__card" id="inloggningshistorik">
        <h5 style={{ margin: '0 0 0.375rem', fontSize: '1rem', fontWeight: 600 }}>Inloggningshistorik</h5>
        <p style={{ color: '#737B8B', margin: '0 0 1rem', fontSize: '0.875rem' }}>
          Senaste aktivitet på ditt konto. Ser du något du inte känner igen —
          aktivera lösenordsinloggning och byt lösenord i ditt Google-konto.
        </p>

        {loginEventsLoading && (
          <p style={{ margin: 0, color: '#737B8B', fontSize: '0.875rem' }}>Läser in...</p>
        )}

        {!loginEventsLoading && loginEvents && loginEvents.length === 0 && (
          <p style={{ margin: 0, color: '#737B8B', fontSize: '0.875rem' }}>
            Inga händelser ännu. Information sparas från och med nu.
          </p>
        )}

        {!loginEventsLoading && loginEvents && loginEvents.length > 0 && (
          <div className="user-settings__events">
            {loginEvents.map((evt) => (
              <div key={evt.event_id} className="user-settings__event-row">
                <div className="user-settings__event-main">
                  <span
                    className="user-settings__event-type"
                    style={{ color: EVENT_COLORS[evt.event_type] || '#737B8B' }}
                  >
                    {EVENT_LABELS[evt.event_type] || evt.event_type}
                  </span>
                  <span className="user-settings__event-meta">
                    {summariseUserAgent(evt.user_agent)}
                    {evt.ip_address ? ` · ${evt.ip_address}` : ''}
                    {evt.city || evt.country ? ` · ${[evt.city, evt.country].filter(Boolean).join(', ')}` : ''}
                  </span>
                </div>
                <span className="user-settings__event-time">
                  {formatDateTime(evt.occurred_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default SecuritySection;
